import { MMButton, PendingIcon } from '@components'
import { Success, XMark } from '@icons'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useState } from '@integrations/react'
import { extractIbanDetails, formatIbanAddress } from 'mmfintech-backend-api'
import type { IbanIssueStatus, PaymentResponse } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'

import styles from './IbanRequest.module.scss'

interface Props {
  status: IbanIssueStatus
  iban?: string
  response?: PaymentResponse
  onViewAccounts: () => void
  onIssueAnother: () => void
  onContactSupport: () => void
}

function CopyButton({ value }: { value?: string }) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    if (!value) return
    navigator.clipboard?.writeText(value)
    setCopied(true)
    toast.remove()
    toast.success(tr('FRONTEND.IBAN_REQUEST.COPIED', 'IBAN copied to clipboard.'))
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <MMButton
      type='button'
      variant='primary'
      size='small'
      caption={copied ? tr('FRONTEND.BUTTONS.COPIED', 'Copied') : tr('FRONTEND.BUTTONS.COPY', 'Copy')}
      onClick={handleCopy}
      className={cn(styles['iban-copy-btn'])}
      data-test='iban-copy'
    />
  )
}

function StatusStep({ status, iban, response, onViewAccounts, onIssueAnother, onContactSupport }: Props) {
  // Show every account detail the provider returned (if available).
  const details = status === 'issued' ? extractIbanDetails(response) : undefined
  const detailRows: { label: string; value?: string }[] = details
    ? [
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.BIC', 'BIC / SWIFT'), value: details.bicSwift },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.ACCOUNT_NUMBER', 'Account number'), value: details.accountNumber },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.BANK_CODE', 'Bank code'), value: details.bankCode },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.BRANCH_CODE', 'Branch code'), value: details.branchCode },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.HOLDER_NAME', 'Account holder'), value: details.holderName },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.BANK_NAME', 'Bank name'), value: details.bankName },
        { label: tr('FRONTEND.IBAN_REQUEST.DETAILS.COUNTRY', 'Country'), value: details.country },
        {
          label: tr('FRONTEND.IBAN_REQUEST.DETAILS.HOLDER_ADDRESS', 'Holder address'),
          value: formatIbanAddress(details.holderAddress)
        },
        {
          label: tr('FRONTEND.IBAN_REQUEST.DETAILS.BANK_ADDRESS', 'Bank address'),
          value: formatIbanAddress(details.bankAddress)
        }
      ].filter((row) => row.value && row.value.length > 0)
    : []

  return (
    <div className={cn(styles['iban-step-view'])}>
      <div className={cn(styles['iban-status-body'])}>
        <div className={cn(styles['iban-status-icon'], styles[status])}>
          {status === 'issued' && <Success />}
          {status === 'pending' && <PendingIcon />}
          {status === 'failed' && <XMark />}
        </div>

        {status === 'issued' && (
          <>
            <div className={cn(styles['iban-status-title'])}>
              {tr('FRONTEND.IBAN_REQUEST.ISSUED_TITLE', 'IBAN issued')}
            </div>
            <div className={cn(styles['iban-status-sub'])}>
              {tr('FRONTEND.IBAN_REQUEST.ISSUED_SUB', 'Your IBAN is ready to use. Share it to receive payments.')}
            </div>
            <div className={cn(styles['iban-display'])}>
              <span className={cn(styles['iban-string'])} data-test='iban-string'>
                {iban}
              </span>
              <CopyButton value={iban} />
            </div>

            {detailRows.length > 0 && (
              <div className={cn(styles['iban-details'])} data-test='iban-details'>
                {detailRows.map((row) => (
                  <div className={cn(styles['iban-detail-row'])} key={row.label}>
                    <span className={cn(styles['iban-detail-label'])}>{row.label}</span>
                    <span className={cn(styles['iban-detail-value'])}>{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {status === 'pending' && (
          <>
            <div className={cn(styles['iban-status-title'])}>
              {tr('FRONTEND.IBAN_REQUEST.PENDING_TITLE', 'IBAN request submitted')}
            </div>
            <div className={cn(styles['iban-status-sub'])}>
              {tr(
                'FRONTEND.IBAN_REQUEST.PENDING_SUB',
                "We're processing your request with our partner bank. You'll receive a confirmation within 24 hours."
              )}
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className={cn(styles['iban-status-title'])}>
              {tr('FRONTEND.IBAN_REQUEST.FAILED_TITLE', "We couldn't issue your IBAN")}
            </div>
            <div className={cn(styles['iban-status-sub'])}>
              {(response as { message?: string })?.message ||
                tr(
                  'FRONTEND.IBAN_REQUEST.FAILED_SUB',
                  'Something went wrong with our partner bank. Any fee charged will be refunded within 24 hours.'
                )}
            </div>
          </>
        )}

        <div className={cn(styles['iban-status-actions'])}>
          {status === 'issued' && (
            <>
              <MMButton
                type='button'
                variant='primary'
                caption={tr('FRONTEND.IBAN_REQUEST.VIEW_ACCOUNTS', 'View in Accounts')}
                onClick={onViewAccounts}
                data-test='iban-view-accounts'
                fullWidth
              />
              <MMButton
                type='button'
                variant='secondary'
                caption={tr('FRONTEND.IBAN_REQUEST.ISSUE_ANOTHER', 'Issue another IBAN')}
                onClick={onIssueAnother}
                data-test='iban-issue-another'
                fullWidth
              />
            </>
          )}

          {status === 'pending' && (
            <MMButton
              type='button'
              variant='primary'
              caption={tr('FRONTEND.BUTTONS.DONE', 'Done')}
              onClick={onViewAccounts}
              data-test='iban-done'
              fullWidth
            />
          )}

          {status === 'failed' && (
            <>
              <MMButton
                type='button'
                variant='primary'
                caption={tr('FRONTEND.IBAN_REQUEST.CONTACT_SUPPORT', 'Contact support')}
                onClick={onContactSupport}
                data-test='iban-contact-support'
                fullWidth
              />
              <MMButton
                type='button'
                variant='secondary'
                caption={tr('FRONTEND.BUTTONS.TRY_AGAIN', 'Try again')}
                onClick={onIssueAnother}
                data-test='iban-try-again'
                fullWidth
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default StatusStep
